import { FC, HTMLAttributes } from 'react'

import { EmbroideryThread, Warning } from '@syconium/little-miss-figgy'

import { ProductCatalogEmbroideryPlacement } from '../../../__generated__/graphql/catalog/graphql'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { deriveNameFromEmbroideryPlacement } from '../../../containers/EmbroideryContainer/utils/deriveNameFromEmbroideryPlacement'
import { toEmbroideryFontFamily } from '../../../lib/embroideryFonts'
import { useEmbroideryColorFeedback } from '../useEmbroideryColorFeedback'
import { getEmbroideryNudgedColor } from '../utils'

import { ColorFeedbackLine, DetailLine, TextEmbroideryVisual } from './styles'
import { Template } from './Template'

export interface Props extends HTMLAttributes<HTMLDivElement> {
  backgroundColor: string | undefined
  textColorDisplayName: string | undefined
  textColorCssValue: string | undefined
  textFirstLine: string | undefined
  textFontDisplayName: string | undefined
  textFontCssFamily: string | undefined
  textPlacement: ProductCatalogEmbroideryPlacement | string | undefined
  textSecondLine: string | undefined
}

export const TextSneakPeek: FC<Props> = ({
  backgroundColor,
  textColorDisplayName,
  textColorCssValue,
  textFirstLine,
  textFontDisplayName,
  textFontCssFamily,
  textPlacement,
  textSecondLine,
  ...rest
}) => {
  const previewTextColor = getEmbroideryNudgedColor(backgroundColor, textColorCssValue)
  const colorFeedback = useEmbroideryColorFeedback({
    backgroundColor,
    threadColor: textColorCssValue,
  })

  const placementString =
    typeof textPlacement === 'undefined'
      ? undefined
      : typeof textPlacement === 'string'
      ? textPlacement
      : deriveNameFromEmbroideryPlacement(textPlacement)

  const {
    embroidery: {
      summary: {
        textSecondLineLabelLine,
        textFirstLineLabelLine,
        textFontLabelLine,
        textColorLabelLine,
        textPlacementLabelLine,
      },
    },
  } = useTranslation()

  const textFirstLineFixture = textFirstLine
    ? textFirstLineLabelLine.replace('{{textFirstLine}}', textFirstLine)
    : undefined
  const textSecondLineFixture = textSecondLine
    ? textSecondLineLabelLine.replace('{{textSecondLine}}', textSecondLine)
    : undefined
  const textFontLabelFixture = textFontDisplayName
    ? textFontLabelLine.replace('{{textFont}}', textFontDisplayName)
    : undefined
  const textColorLabelFixture = textColorDisplayName
    ? textColorLabelLine.replace('{{textColor}}', textColorDisplayName)
    : undefined
  const textPlacementLabelFixture = placementString
    ? textPlacementLabelLine.replace('{{textPlacement}}', placementString)
    : undefined

  if (!textPlacementLabelFixture) return null

  return (
    <Template.Container {...rest}>
      <Template.Header backgroundColor={backgroundColor}>
        {textFirstLine && (
          <TextEmbroideryVisual
            $fontFamily={toEmbroideryFontFamily(textFontCssFamily)}
            $textColor={previewTextColor?.toLowerCase()}
          >
            <span>{textFirstLine}</span>
            {textSecondLine && <span>{textSecondLine}</span>}
          </TextEmbroideryVisual>
        )}
      </Template.Header>

      <Template.Body>
        {colorFeedback && (
          <ColorFeedbackLine
            role={colorFeedback.tone === 'caution' ? 'alert' : 'status'}
            $tone={colorFeedback.tone}
          >
            {colorFeedback.message}
            {colorFeedback.icon === 'thread' ? (
              <EmbroideryThread height={'16px'} width={'16px'} />
            ) : (
              <Warning height={'16px'} width={'16px'} />
            )}
          </ColorFeedbackLine>
        )}
        {textFirstLineFixture && <DetailLine $general>{textFirstLineFixture}</DetailLine>}
        {textSecondLineFixture && <DetailLine $general>{textSecondLineFixture}</DetailLine>}
        {textFontLabelFixture && <DetailLine>{textFontLabelFixture}</DetailLine>}
        {textColorLabelFixture && <DetailLine>{textColorLabelFixture}</DetailLine>}
        {textPlacementLabelFixture && <DetailLine>{textPlacementLabelFixture}</DetailLine>}
      </Template.Body>
    </Template.Container>
  )
}
